import { ActionContext, Module } from 'vuex'
import { Engine, TopLevelCondition } from 'json-rules-engine'
import {
  IReportingNotification,
  IReportingNotificationViewed,
  NotificationStatusType,
  VisibilityLevel,
} from '../util/notification/notification'
import { IReportingNotificationState, RootState } from './state_models'

import { reportingNotificationService } from '../services/ReportingService'
import { subAccountRoutes } from '@/routes/constants/agency/subAccounts'
import { useEmitter } from '../pmd/composition/useEmitter'

let controller: AbortController | undefined
let signal: AbortSignal | undefined

interface IOptions {
  user: any
  location: any
  company: any
  route: any
}

const initialState: IReportingNotificationState = {
  firstLoadCompleted: false,
  loading: false,
  showNotification: false,
  forceClosedBanner: false,
  allNotificationData: [],
  conditionFilteredNotifications: [],
  apiFilteredNotifications: [],
  visibleBannerNotifications: [],
  visibleDrawerNotifications: [],
  currentLocationId: '',
  showNoNetworkNotification: false,
  pendoViewedNotificationIds: [],
}

let ruleEngine = new Engine([], { allowUndefinedFacts: true, allowUndefinedConditions: true })

/**
 * Checks if a DOM element is actually visible to the user
 * @param elementId - The ID of the element to check
 * @returns boolean - true if element is visible, false if hidden or not present
 */
function isElementVisibleInDOM(elementId: string): boolean {
  if (typeof window === 'undefined') {
    return true
  }

  const element = document.getElementById(elementId)
  if (!element) return false // Element doesn't exist in DOM

  const computedStyle = window.getComputedStyle(element)

  return (
    computedStyle.display !== 'none' && computedStyle.visibility !== 'hidden' && parseFloat(computedStyle.opacity) > 0.1
  )
}

export const notification: Module<IReportingNotificationState, RootState> = {
  namespaced: true,
  state: JSON.parse(JSON.stringify(initialState)),
  getters: {
    get: (state: IReportingNotificationState) => {
      return state
    },
  },
  mutations: {
    setAllNotification: (
      state: IReportingNotificationState,
      payload: { allNotificationData: IReportingNotification[] }
    ) => {
      state.allNotificationData = payload.allNotificationData || []
    },
    setConditionFilteredNotifications: (
      state: IReportingNotificationState,
      payload: { conditionFilteredNotifications: IReportingNotificationViewed[] }
    ) => {
      state.conditionFilteredNotifications = payload.conditionFilteredNotifications || []
    },
    setApiFilteredNotifications: (
      state: IReportingNotificationState,
      payload: { apiFilteredNotifications: IReportingNotificationViewed[] }
    ) => {
      state.apiFilteredNotifications = payload.apiFilteredNotifications || []
    },
    setVisibleBannerNotifications: (
      state: IReportingNotificationState,
      payload: { visibleBannerNotifications: IReportingNotificationViewed[] }
    ) => {
      state.visibleBannerNotifications = payload.visibleBannerNotifications || []
    },
    setVisibleDrawerNotifications: (
      state: IReportingNotificationState,
      payload: { visibleDrawerNotifications: IReportingNotificationViewed[] }
    ) => {
      state.visibleDrawerNotifications = payload.visibleDrawerNotifications || []
    },
    setShowNotification: (state: IReportingNotificationState, showNotification: boolean) => {
      state.showNotification = showNotification
    },
    setShowNoNetworkNotification: (state: IReportingNotificationState, showNoNetworkNotification: boolean) => {
      state.showNoNetworkNotification = showNoNetworkNotification
    },
    setLoading: (state: IReportingNotificationState, loading: boolean) => {
      state.loading = loading
    },
    setFirstLoadCompleted: (state: IReportingNotificationState, firstLoadCompleted: boolean) => {
      state.firstLoadCompleted = firstLoadCompleted
    },
    setForceClosedBanner: (state: IReportingNotificationState, forceClosedBanner: boolean) => {
      state.forceClosedBanner = forceClosedBanner
    },
    setCurrentLocationId: (state: IReportingNotificationState, currentLocationId: string) => {
      state.currentLocationId = currentLocationId
    },
    setPendoViewedNotificationIds: (state: IReportingNotificationState, ids: string[]) => {
      state.pendoViewedNotificationIds = ids
    },
  },
  actions: {
    async setNewLocationId(context: ActionContext<IReportingNotificationState, RootState>, newLocationId: string) {
      context.commit('setCurrentLocationId', newLocationId)
    },
    async syncAll(context: ActionContext<IReportingNotificationState, RootState>, options: IOptions) {
      try {
        context.dispatch('setLoading', true)
        context.dispatch('reset')

        if (controller) controller.abort()
        controller = new AbortController()
        signal = controller.signal

        const notificationData = await fetchNotificationData(options)
        context.commit('setAllNotification', {
          allNotificationData: notificationData,
        })
        context.commit('setForceClosedBanner', false)

        options = {
          ...options,
          location: context.state.currentLocationId ? context.rootState?.locations?.currentLocation : null,
        }

        context.dispatch('filterNotificationByCondition', options)
        if (notificationData?.length) context.commit('setFirstLoadCompleted', true)
      } catch {
        return
      }
    },
    async addPendoViewedNotificationIds(
      context: ActionContext<IReportingNotificationState, RootState>,
      pendoViewedNotificationId: string
    ) {
      context.commit('setPendoViewedNotificationIds', [
        ...context.state.pendoViewedNotificationIds,
        pendoViewedNotificationId,
      ])
    },
    async filterNotificationByCondition(
      context: ActionContext<IReportingNotificationState, RootState>,
      options: IOptions
    ) {
      try {
        const notificationByCondition = await getNotificationByCondition(context.state.allNotificationData, options)

        context.commit('setConditionFilteredNotifications', {
          conditionFilteredNotifications: notificationByCondition,
        })
        context.dispatch('filterNotificationByAPI', options)
      } catch {
        return
      }
    },
    async filterNotificationByAPI(context: ActionContext<IReportingNotificationState, RootState>, options: IOptions) {
      try {
        const notificationByAPI = await filterNotificationByAPI(
          {
            notificationData: context.state.conditionFilteredNotifications,
            locationId: context.state.currentLocationId,
          },
          options
        )

        context.commit('setApiFilteredNotifications', {
          apiFilteredNotifications: notificationByAPI,
        })

        context.dispatch('setVisibleNotification', options)
      } catch {
        return
      }
    },
    async setVisibleNotification(context: ActionContext<IReportingNotificationState, RootState>, options: IOptions) {
      try {
        const visibleNotification = await setVisibleNotification(context.state.apiFilteredNotifications, options)
        const visibleDrawerNotifications = await setVisibleNotification(
          context.state.apiFilteredNotifications,
          options,
          true
        )
        context.commit('setVisibleBannerNotifications', {
          visibleBannerNotifications: visibleNotification,
        })
        context.commit('setVisibleDrawerNotifications', {
          visibleDrawerNotifications: visibleDrawerNotifications,
        })
        const emitter = useEmitter()
        emitter.emit('notification:visibleBannerNotifications', visibleNotification)
        emitter.emit('notification:visibleDrawerNotifications', visibleDrawerNotifications)

        context.dispatch('setShowNotification', { showNotification: visibleNotification.length > 0 })
        context.dispatch('setLoading', false)
      } catch {
        return
      }
    },
    async setNotificationViewed(
      context: ActionContext<IReportingNotificationState, RootState>,
      notificationId: string
    ) {
      try {
        const allNotificationIndex = context.state.allNotificationData.findIndex(
          (notification: any) => notification.id === notificationId
        )
        if (allNotificationIndex >= 0) {
          const allNotificationData = [...context.state.allNotificationData]
          allNotificationData[allNotificationIndex].viewed = true
          context.commit('setAllNotification', { allNotificationData })
        }

        const conditionFilteredNotificationIndex = context.state.conditionFilteredNotifications.findIndex(
          (notification: any) => notification.id === notificationId
        )
        if (conditionFilteredNotificationIndex >= 0) {
          const conditionFilteredNotifications = [...context.state.conditionFilteredNotifications]
          conditionFilteredNotifications[conditionFilteredNotificationIndex].viewed = true
          context.commit('setConditionFilteredNotifications', { conditionFilteredNotifications })
        }

        const apiFilteredNotificationIndex = context.state.apiFilteredNotifications.findIndex(
          (notification: any) => notification.id === notificationId
        )
        if (apiFilteredNotificationIndex >= 0) {
          const apiFilteredNotifications = [...context.state.apiFilteredNotifications]
          apiFilteredNotifications[apiFilteredNotificationIndex].viewed = true
          context.commit('setApiFilteredNotifications', { apiFilteredNotifications })
        }

        const visibleBannerNotificationIndex = context.state.visibleBannerNotifications.findIndex(
          (notification: any) => notification.id === notificationId
        )
        if (visibleBannerNotificationIndex >= 0) {
          const visibleBannerNotifications = [...context.state.visibleBannerNotifications]
          visibleBannerNotifications[visibleBannerNotificationIndex].viewed = true
          context.commit('setVisibleBannerNotifications', { visibleBannerNotifications })
        }

        const visibleDrawerNotificationIndex = context.state.visibleDrawerNotifications.findIndex(
          (notification: any) => notification.id === notificationId
        )
        if (visibleDrawerNotificationIndex >= 0) {
          const visibleDrawerNotifications = [...context.state.visibleDrawerNotifications]
          visibleDrawerNotifications[visibleDrawerNotificationIndex].viewed = true
          context.commit('setVisibleDrawerNotifications', { visibleDrawerNotifications })
        }
      } catch {
        return
      }
    },
    async reset(context: ActionContext<IReportingNotificationState, RootState>) {
      context.dispatch('setShowNotification', { showNotification: false })

      context.commit('setAllNotification', { allNotificationData: [] })
      context.commit('setConditionFilteredNotifications', { conditionFilteredNotifications: [] })
      context.commit('setApiFilteredNotifications', { apiFilteredNotifications: [] })
      context.commit('setVisibleBannerNotifications', { visibleBannerNotifications: [] })
      context.commit('setVisibleDrawerNotifications', { visibleDrawerNotifications: [] })
      context.commit('setPendoViewedNotificationIds', [])
    },
    async setShowNotification(
      context: ActionContext<IReportingNotificationState, RootState>,
      options: {
        showNotification: boolean
        showNoNetworkNotification?: boolean
      }
    ) {
      let { showNotification, showNoNetworkNotification } = options
      showNoNetworkNotification = showNoNetworkNotification ?? false
      showNotification = !context.state.forceClosedBanner && showNotification

      // Note: Visibility check is handled by the Vue component after mounting

      context.commit('setShowNotification', showNotification || showNoNetworkNotification)
      context.commit('setShowNoNetworkNotification', showNoNetworkNotification)

      const emitter = useEmitter()
      emitter.emit('notification:showNotification', showNotification || showNoNetworkNotification)
      emitter.emit('notification:showNoNetworkNotification', showNoNetworkNotification)
    },
    async setLoading(context: ActionContext<IReportingNotificationState, RootState>, loading: boolean) {
      context.commit('setLoading', loading)

      const emitter = useEmitter()
      emitter.emit('notification:loading', loading)
    },
    async setForceClosedBanner(context: ActionContext<IReportingNotificationState, RootState>) {
      context.commit('setForceClosedBanner', true)
      context.dispatch('setShowNotification', { showNotification: true })

      const emitter = useEmitter()
      emitter.emit('notification:forceClosedBanner', true)
    },
    async checkBannerVisibilityAfterMount(context: ActionContext<IReportingNotificationState, RootState>) {
      if (context.state.showNotification) {
        const isActuallyVisible = isElementVisibleInDOM('notification_banner-top_bar')
        if (!isActuallyVisible) {
          context.commit('setShowNotification', false)
          context.commit('setShowNoNetworkNotification', false)

          const emitter = useEmitter()
          emitter.emit('notification:showNotification', false)
          emitter.emit('notification:showNoNetworkNotification', false)
        }
      }
    },
  },
}

function resetRuleEngine(options: IOptions) {
  ruleEngine = new Engine([], { allowUndefinedFacts: true, allowUndefinedConditions: true })
  // this is a custom operator to check if the fact value is truthy
  // this is useful when you want to check if a fact value is not null, undefined, 0, false, empty string, NaN etc.
  ruleEngine.addOperator('truthy', factValue => {
    return !!factValue
  })

  ruleEngine.addFact('user', options.user)
  ruleEngine.addFact('company', options.company)
  ruleEngine.addFact('location', options.location)
  ruleEngine.addFact('route', options.route)
}

async function setVisibleNotification(
  notificationData: IReportingNotificationViewed[],
  options: IOptions,
  showFutureNotification = false
) {
  resetRuleEngine(options)
  const visibilityLevel = options.route?.params?.location_id ? VisibilityLevel.Account : VisibilityLevel.Agency
  for (const notification of notificationData) {
    if (!showFutureNotification && notification.nextScheduledAt && notification.nextScheduledAt > Date.now() / 1000)
      continue
    const visibleNotification = notification.visibility.find(v => v.type === visibilityLevel)
    if (visibleNotification) {
      ruleEngine.addRule({
        conditions: visibleNotification.conditions || { any: [] },
        event: { type: notification.id, params: notification },
      })
    }
  }
  const results = await ruleEngine.run()
  if (results.events.length) {
    const updatedNotifications = results?.events.map(result => result.params)
    return updatedNotifications
      .filter((notification: any) => !!notification.title)
      .sort((a: any, b: any) => {
        // Always prioritize revex-company-tnc-banner notification
        if (a.alias === 'revex-company-tnc-banner' && b.alias !== 'revex-company-tnc-banner') return -1
        if (b.alias === 'revex-company-tnc-banner' && a.alias !== 'revex-company-tnc-banner') return 1

        // First, sort by priority
        if (a.priority !== b.priority) {
          return a.priority - b.priority
        }

        // Then, check for route-specific conditions
        const aVisibility = a.visibility.find(v => v.type === visibilityLevel)
        const bVisibility = b.visibility.find(v => v.type === visibilityLevel)

        const aHasRouteCondition = hasRouteCondition(aVisibility?.conditions, options.route.name)
        const bHasRouteCondition = hasRouteCondition(bVisibility?.conditions, options.route.name)

        // Prioritize notifications with matching route conditions
        if (aHasRouteCondition && !bHasRouteCondition) return -1
        if (!aHasRouteCondition && bHasRouteCondition) return 1

        // If both have or don't have route conditions, maintain current order
        return 0
      })
  }
  return []
}

const hasRouteCondition = (conditions: TopLevelCondition, routeName: string): boolean => {
  if (!conditions) return false

  const checkConditions = (cond: any): boolean => {
    if (cond.fact === 'route' && cond.path === '$.name') {
      const routeValue = cond.value
      if (typeof routeValue === 'string') {
        return routeValue === routeName
      }
      if (Array.isArray(routeValue)) {
        return routeValue.includes(routeName)
      }
    }
    return false
  }

  const traverse = (node: any): boolean => {
    if (!node) return false
    if (checkConditions(node)) return true
    if (node.all) return node.all.some(traverse)
    if (node.any) return node.any.some(traverse)
    return false
  }

  return traverse(conditions)
}

async function filterNotificationByAPI(
  { notificationData, locationId }: { notificationData: IReportingNotificationViewed[]; locationId: string },
  options: IOptions
) {
  if (!notificationData.length) return
  const updatedNotifications: any = []
  const externalConfigNotification: any = []
  let notificationPromises: any = []
  for (const notification of notificationData) {
    const isAgencyNotification = notification.visibility.find(v => v.type === VisibilityLevel.Agency)
    const canFetchNotification =
      isAgencyNotification ||
      (!isAgencyNotification && locationId && options.route.name !== subAccountRoutes.accounts.detail)
    if (notification.externalAPIConfig && canFetchNotification) {
      externalConfigNotification.push(notification)
      notificationPromises.push(
        reportingNotificationService.getNotificationByConfig(
          {
            ...notification.externalAPIConfig,
            query: {
              ...notification.externalAPIConfig.query,
              locationId: options.location?.id,
              userId: options.user.id,
              companyId: options.company.id,
            },
          },
          signal
        )
      )
    } else {
      updatedNotifications.push(notification)
    }
  }
  notificationPromises = await Promise.allSettled(notificationPromises)
  for (let i = 0; i < notificationPromises.length; i++) {
    if (notificationPromises[i].status === 'rejected' && notificationPromises[i].reason?.code === 'ERR_CANCELED') {
      return []
    }
    if (notificationPromises[i].status === 'fulfilled' && notificationPromises[i].value?.show) {
      const primaryButton = {
        ...(externalConfigNotification[i].primaryButton ?? {}),
        ...(notificationPromises[i].value.primaryButton ?? {}),
      }
      const updatedNotification = {
        ...externalConfigNotification[i],
        ...notificationPromises[i].value,
        primaryButton: Object.keys(primaryButton).length ? primaryButton : undefined,
      }
      updatedNotifications.push(updatedNotification)
    }
  }
  return updatedNotifications.sort((a, b) => {
    // First, sort by priority
    if (a.priority !== b.priority) {
      return a.priority - b.priority
    }

    // If priorities are the same, sort by type (account first)
    return a.visibility.find(v => v.type === VisibilityLevel.Agency) ? 1 : -1
  })
}

async function getNotificationByCondition(notificationData: IReportingNotificationViewed[], options: IOptions) {
  resetRuleEngine(options)
  for (const notification of notificationData) {
    ruleEngine.addRule({
      conditions: notification.conditions || { any: [] },
      event: { type: notification.id, params: notification },
    })
  }

  const results = await ruleEngine.run()
  return results?.events.map(result => result.params)
}

async function fetchNotificationData(options: IOptions) {
  try {
    const response = await reportingNotificationService.getAllNotifications(
      {
        notificationStatus: NotificationStatusType.NotViewed,
        locationId: options.route.name === subAccountRoutes.accounts.detail ? null : options.location?.id,
        companyId: options.company.id,
      },
      signal
    )
    return response?.notifications || []
  } catch (error) {
    console.error('Error fetching  notification data', error)
    return []
  }
}
